import NodeGroupService from "../../service/nodeGroup.service";
import UserService from "../../service/user.service";

/**
 * Resolve the id of a freshly-created node group. Prefer the id returned by
 * the POST response; if the response shape doesn't include it, fall back to
 * fetching the full list and taking the last entry (the project's existing
 * convention — has a small race risk with concurrent creates, but matches
 * the legacy behaviour in ManageNodes.js line ~496).
 */
async function resolveNewGroupId(token, createResponse) {
  const data = createResponse && createResponse.data;
  if (data && (data.id || data._id)) return data.id || data._id;
  const listRes = await NodeGroupService.getNodeGroupList(token);
  const groups = (listRes && listRes.data && listRes.data.groups) || [];
  if (groups.length === 0) return undefined;
  return groups[groups.length - 1]._id || groups[groups.length - 1].id;
}

/**
 * Create a node group and bind it to one or more user groups in a single
 * coordinated flow. The node group is created first; on failure, no binds
 * are attempted. Bind failures are collected per-user-group and reported
 * back without rollback (the group already exists; user can rebind via
 * existing UI).
 *
 * @param {string} token auth token
 * @param {string} name node group name
 * @param {string[]} tags node group tags (existing API param)
 * @param {string[]} userGroupIds user group ids to bind to
 * @returns {Promise<{
 *   status: "ok"|"create_failed",
 *   error?: Error,
 *   newGroupId?: string,
 *   bindResults?: Array<{ugId: string, status: "ok"|"bind_failed", error?: Error}>
 * }>}
 */
export async function createNodeGroupAndBind(token, name, tags, userGroupIds) {
  let createResponse;
  try {
    createResponse = await NodeGroupService.createNewNodeGroup(token, name, tags);
  } catch (err) {
    return { status: "create_failed", error: err };
  }

  const newGroupId = await resolveNewGroupId(token, createResponse);

  const bindResults = [];
  for (const ugId of userGroupIds) {
    try {
      await UserService.addNodeGroupToUserGroup(token, ugId, newGroupId);
      bindResults.push({ ugId, status: "ok" });
    } catch (err) {
      bindResults.push({ ugId, status: "bind_failed", error: err });
    }
  }

  return { status: "ok", newGroupId, bindResults };
}
